import { isUnknownRecord } from '../validation/unknown-record';
import { readAnswer } from '../programs/program-key';
import { isDerivedAnswerKey, relativizeAnswerPointer } from './derived-targets';
import type { AnswerScalar, NormalisedOnboardingAnswer } from './schema';

/**
 * One read of `onboarding_sessions.answers`, with the four judgements every
 * consumer currently re-derives already made.
 *
 * The blob is flow-version-dependent jsonb written by a GenUI flow, so reading
 * it means deciding: which of four persisted key shapes holds an answer, which
 * keys are the member's and which are a tool's generated output, what a stored
 * code means in display text, and what counts as an answered value at all.
 * Seven consumers made those calls separately, and the ones that got any of
 * them subtly wrong disagreed with the other six about the same member.
 *
 * This module makes the first, second and fourth once. The THIRD — what a code
 * MEANS in words — is deliberately NOT here and does not cross this contract at
 * all: that is presentation, it stays with the surface that renders it, and the
 * consumer reads `value` (and the resolved program keys beside it) to decide
 * its own wording.
 *
 * Pure: no I/O, no clock, no telemetry. The stored blob is member PHI and must
 * not reach a log or a span attribute (ADR-069).
 */

/** One scalar leaf of a stored value, and where inside that value it sits. */
export interface AnswerScalarAt {
  /**
   * RFC 6901-style pointer INTO the stored value — `''` when the value is
   * itself a scalar, `/0` for the first element of an array, `/a/1` for a
   * nested one.
   *
   * Present so a consumer can attribute a scalar back to its position rather
   * than matching on the value, which is how one answer came to vouch for
   * another's: two keys storing the same unlabellable number made a rendered
   * one answer the lookup for a lost one.
   */
  path: string;
  value: AnswerScalar;
}

/**
 * Whether a stored value is an answer a reader could be shown.
 *
 * ONE definition, exported, because every consumer needs the same answer and a
 * second copy is what broke: a week strip counting `raw !== null` while an
 * answer list separately treated an untouched text box as silence made the
 * totals announce a day the list could render nowhere.
 *
 * An empty string, an empty array and an empty object are silence rather than a
 * claim, and silence at any depth is still silence — a shallow length check
 * called `['', '']` and `{ a: {} }` recorded answers. `0` and `false` are
 * answers: those are claims the member made, not absences.
 */
export function holdsAnswer(value: unknown): boolean {
  // Iterative, not recursive, for the same reason as `answerScalars` below: the
  // blob is untrusted jsonb with nothing constraining its depth, and a
  // recursive walk overflows the stack on a pathologically nested value — which
  // on this path means one malformed row fails a clinical read. A depth CAP
  // would not do either: it makes the judgement depend on where the cap falls.
  const pending: unknown[] = [value];
  while (pending.length > 0) {
    const current = pending.pop();
    if (current === undefined || current === null) continue;
    if (typeof current === 'string') {
      if (current.trim().length > 0) return true;
      continue;
    }
    if (Array.isArray(current)) {
      for (const item of current) pending.push(item);
      continue;
    }
    if (typeof current === 'object') {
      for (const nested of Object.values(current)) pending.push(nested);
      continue;
    }
    // Every remaining scalar is an answer, `0` and `false` included: those are
    // claims the member made, not absences.
    return true;
  }
  return false;
}

/** Escape a path segment for an RFC 6901 pointer (`~` → `~0`, `/` → `~1`). */
function escapePointerSegment(segment: string): string {
  return segment.replace(/~/g, '~0').replace(/\//g, '~1');
}

/**
 * Every scalar leaf stored under a value, in stored order, each with its path.
 *
 * The unit of the coverage guarantee. A KEY is not the unit — a key can hold a
 * list, and a consumer can account for some of that list and not the rest.
 * Comparing what was accounted for against what was stored in the same unit is
 * what removes that whole class of mistake rather than its current instance.
 *
 * `null` and `undefined` contribute no leaf: they are absence, not a value. An
 * empty string DOES contribute one — it is a leaf that exists — which is a
 * different question from whether it is an answer (`holdsAnswer`).
 */
export function answerScalars(value: unknown): AnswerScalarAt[] {
  // Iterative for the same reason as `holdsAnswer`: a recursive walk over
  // untrusted jsonb overflows the stack on a pathologically nested value. A
  // depth cap would trade the crash for a silently truncated list, which is
  // worse here — the one-to-one correspondence with what the record holds is
  // the whole guarantee this function exists to make.
  const out: AnswerScalarAt[] = [];
  // A stack, with children pushed in reverse so popping yields stored order.
  const pending: Array<{ value: unknown; path: string }> = [{ value, path: '' }];
  while (pending.length > 0) {
    const current = pending.pop();
    if (current === undefined) break;
    const node = current.value;
    if (node === undefined || node === null) continue;
    if (Array.isArray(node)) {
      for (let index = node.length - 1; index >= 0; index -= 1) {
        pending.push({ value: node[index], path: `${current.path}/${index}` });
      }
      continue;
    }
    if (typeof node === 'object') {
      const entries = Object.entries(node);
      for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (entry === undefined) continue;
        pending.push({
          value: entry[1],
          path: `${current.path}/${escapePointerSegment(entry[0])}`,
        });
      }
      continue;
    }
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
      out.push({ path: current.path, value: node });
    }
    // Anything else a jsonb round-trip cannot produce (a function, a symbol) is
    // deliberately dropped rather than coerced: inventing a leaf for it would
    // break the one-to-one correspondence this list exists to guarantee.
  }
  return out;
}

/** A stored answer under its canonical flat key, exactly as stored. */
export interface CanonicalAnswerEntry {
  key: string;
  value: unknown;
}

/** The container key the flow writes when it persists state as a nested object. */
const FLOW_STATE_CONTAINER_KEY = 'screen';

/**
 * The flat key a stored key refers to, across every shape the reader accepts.
 *
 * `/screen/goal`, `/goal` and `goal` are one answer under three spellings;
 * collapsing them here is what keeps a consumer from listing the same answer
 * three times. Delegates to the registry's own pointer rule rather than
 * restating it — this module's whole argument is that a second copy of a rule
 * is what drifts.
 */
const canonicaliseKey = relativizeAnswerPointer;

/**
 * Every answer the record holds, one entry per canonical key, at the shared
 * reader's precedence.
 *
 * The precedence is `readAnswer`'s and is delegated to it rather than restated:
 * flat `key` wins, then `/key`, then `/screen/key`, then a member of the
 * `screen` container. A record carrying two spellings of one key therefore has
 * ONE effective answer — the one every consumer acts on — and the shadowed
 * value is deliberately absent, because presenting it would show a reader
 * something no consumer will ever use.
 *
 * A non-object `screen` value is somebody's answer to a question called
 * "screen", not a container, so it stays an ordinary entry.
 */
export function canonicalAnswerEntries(answers: unknown): CanonicalAnswerEntry[] {
  if (!isUnknownRecord(answers)) return [];

  // An OBJECT `screen` is the flow-state container whatever it holds — an empty
  // one is a container with nothing in it, not somebody's answer to a question
  // called "screen". Keying off its contents instead emitted `{ screen: {} }`
  // as an ordinary entry, putting a storage artefact in front of a reader.
  // Only a NON-object `screen` is a real answer.
  const container = answers[FLOW_STATE_CONTAINER_KEY];
  const hasContainer = isUnknownRecord(container);
  const containerKeys = hasContainer ? Object.keys(container) : [];

  // Stored order is preserved, and the container's members are visited where
  // the container itself sits, so a reader meets the answers in the order the
  // record holds them rather than in an order this function invented.
  const orderedStoredKeys: string[] = [];
  for (const storedKey of Object.keys(answers)) {
    if (storedKey === FLOW_STATE_CONTAINER_KEY && hasContainer) {
      orderedStoredKeys.push(...containerKeys);
      continue;
    }
    orderedStoredKeys.push(storedKey);
  }

  const seen = new Set<string>();
  const entries: CanonicalAnswerEntry[] = [];
  for (const storedKey of orderedStoredKeys) {
    const key = canonicaliseKey(storedKey);
    if (seen.has(key)) continue;
    seen.add(key);
    // `readAnswer`, not `answers[storedKey]`: the stored key we happen to be
    // standing on may be the SHADOWED spelling, and the entry must carry the
    // value the rest of the platform resolves for this key.
    const value = readAnswer(answers, key);
    if (value === undefined) continue;
    entries.push({ key, value });
  }
  return entries;
}

/**
 * Normalise a stored `answers` blob into the typed list.
 *
 * A blob that is not an object — a scalar, an array, `null` — normalises to an
 * empty list rather than throwing. The column is jsonb and nothing constrains
 * its top-level shape, so a malformed row is a state this boundary has to
 * absorb: uncertainty is resolved here, once, and no caller repeats the check
 * (ADR-117 Rule 4).
 */
export function normaliseOnboardingAnswers(answers: unknown): NormalisedOnboardingAnswer[] {
  return canonicalAnswerEntries(answers).map(({ key, value }) => ({
    key,
    origin:
      isDerivedAnswerKey(key) || key === 'usage_capture' || key === 'usage_capture_previous'
        ? 'derived'
        : 'member',
    answered: holdsAnswer(value),
    value,
    scalars: answerScalars(value),
  }));
}
